import {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {AdsApi} from "./AdsApi";
import {
	MAX_PURCHASE_POLLS,
	PURCHASE_POLL_MS,
	forgetSettlingPurchase,
	purchaseIsSettling,
	subscribeSettlingPurchase,
} from "./purchaseConversions";
import {anyPurchasePixelReadyHere, reportRecordedPurchases} from "./purchaseReporters";

/**
 * Reports a completed purchase to each advertising pixel, once per pixel
 * (kitecorp/transferify-api-service#1241, Meta added by kitecorp/transferify#375).
 *
 * A thin adapter by mandate (epic #26): it holds no rules. Whether a purchase is still settling, which
 * of the api's answers each pixel has yet to be told, what a pixel is handed and when the mark is done
 * are `./purchaseConversions`' decisions; which pixels there are is `./purchaseReporters`'; whether
 * anything may be sent at all is each pixel's own.
 *
 * <b>Mounted on the app SHELL, and it has to be.</b> The three purchase surfaces do not all report on the
 * page they complete on. A hosted subscription checkout returns to `/activate/<token>`, which is outside
 * the pixel's route scope because that path carries a single-use token — so the buyer's Purchase can only
 * be sent once they continue, which they do to `/`. A host narrower than the shell would unmount before
 * that. The surfaces themselves say nothing more than "a payment completed here"; this decides where and
 * when it becomes an event.
 *
 * <b>Nothing is requested without consent.</b> The api is asked only while a pixel is RUNNING and may
 * report on this page, which happens on granted advertising consent alone — so a visitor who refused, or
 * has not decided, causes no request at all, not merely an event that is dropped.
 *
 * <b>Order matters at the call site:</b> mount this AFTER `useRedditPixel` and `useMetaPixel`, whose own
 * route effects are what start the pixels on the page a buyer walks into. Effects run in hook-declaration
 * order, so the other way round this would ask "is a pixel running" one render too early.
 */
export function usePurchaseConversions(): void {
	const location = useLocation();
	/**
	 * Bumped whenever a surface marks a purchase as settling.
	 *
	 * Without it this would look exactly once per navigation, and the sponsor return would be missed
	 * every time: that page reads `?booking=` only after it has mounted (the query legitimately differs
	 * from the pre-rendered HTML), and React runs a child's effects before its parent's — so the mark
	 * lands one tick after this shell has already looked and found nothing.
	 */
	const [settlingGeneration, setSettlingGeneration] = useState(0);

	useEffect(() => subscribeSettlingPurchase(
		() => setSettlingGeneration((generation) => generation + 1)), []);

	useEffect(() => {
		if (!purchaseIsSettling() || !anyPurchasePixelReadyHere()) {
			return;
		}
		let disposed = false;
		let attempts = 0;
		let timer: ReturnType<typeof setInterval> | undefined;
		/**
		 * Stops asking on THIS route. Deliberately split from forgetting the purchase: a budget that ran
		 * out is "not yet", not "never", and the mark's own age bound is what decides when it becomes
		 * never. Clearing it here would make that bound unreachable and would lose a purchase whose
		 * webhook merely took longer than half a minute.
		 */
		const stopAsking = () => {
			disposed = true;
			if (timer !== undefined) {
				clearInterval(timer);
			}
		};
		const ask = async () => {
			attempts += 1;
			try {
				const recorded = await AdsApi.recentPurchaseConversions();
				if (disposed) {
					return;
				}
				const verdict = reportRecordedPurchases(recorded);
				if (verdict === "settled") {
					// Every running pixel is told; the mark has done its job and must not arm another route.
					forgetSettlingPurchase();
					stopAsking();
					return;
				}
				if (verdict === "ask-on-next-page") {
					// A running pixel may not report on THIS page; asking here again cannot change that.
					stopAsking();
					return;
				}
			} catch {
				// A failed read is not evidence that nothing was bought; the next tick re-asks. A 401 lands
				// here too, which is the right answer for a session that expired mid-flow.
			}
			if (!disposed && attempts >= MAX_PURCHASE_POLLS) {
				stopAsking();
			}
		};
		void ask();
		timer = setInterval(() => void ask(), PURCHASE_POLL_MS);
		return () => {
			disposed = true;
			clearInterval(timer);
		};
	}, [location.pathname, settlingGeneration]);
}
